import { Icon } from "@mindlercare/ui-web";
import { Typography } from "components/typography";
import { PageContainer } from "../base";
import { Chat, ChatMessage } from "@mindlercare/ui-chatbot";
import { useState } from "react";
import { mindlerApiVanilla } from "api/apiv2";
import { styled } from "styled-components";
import { useTranslation } from "react-i18next";
import { RouterOutput } from "@mindlercare/mindlerapi-client";
import { defaultTheme } from "@mindlercare/ui-web/src/shared/constants";

const StyledPageContainer = styled(PageContainer)`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding-top: 24px;

  @media (max-width: 768px) {
    padding-left: 8px;
    padding-right: 8px;
    padding-top: 16px;
    gap: 16px;
  }
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  width: 100%;
  gap: 8px;
`;

const ChatContainer = styled.div`
  flex: 1;
  width: 100%;
  max-width: 868px;
  min-height: 0;
`;

const DisclaimerText = styled(Typography)`
  text-align: center;
  display: block;
  color: ${defaultTheme.COLORS.GRAY};
`;

let messageCounter = 0;
type ChatbotCreateSessionResponse =
  RouterOutput["chatbot"]["clinicalGuidelines"]["createSession"];

export const ClinicalGuidelines = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const [isAgentReplying, setIsAgentReplying] = useState(false);
  const [messages, setMessage] = useState<ChatMessage[]>([]);
  const [session, setSession] = useState<ChatbotCreateSessionResponse>();
  const [showInformationMessage, setShowInformationMessage] = useState(true);
  const [messageFeedback, setMessageFeedback] = useState<
    Record<string, boolean | undefined>
  >({});

  const locale = navigator.language.split("-")[0];

  const generateId = () => {
    return `${messageCounter++}`;
  };

  const handleThumbsFeedback = (
    messageId: string,
    feedback: boolean | undefined,
  ) => {
    mindlerApiVanilla.chatbot.thumbUpDownMessage
      .mutate({
        chatbotSessionMessagePublicId: messageId,
        thumbsUp: feedback,
      })
      .then(() => {
        setMessageFeedback((prev) => ({
          ...prev,
          [messageId]: feedback,
        }));
      });
  };

  const handleSend = async () => {
    try {
      let currentSession: ChatbotCreateSessionResponse | undefined = session;
      setInput("");
      setIsAgentReplying(true);
      setMessage([
        ...messages,
        { author: "user", id: generateId(), text: input },
      ]);
      if (!currentSession) {
        const session =
          await mindlerApiVanilla.chatbot.clinicalGuidelines.createSession.mutate();
        setSession(session);
        currentSession = session;
      }

      const message = input;
      const reply =
        await mindlerApiVanilla.chatbot.clinicalGuidelines.sendMessage.mutate({
          sessionId: currentSession.sessionId,
          message,
        });
      setMessage((prev) => [
        ...prev,
        {
          author: "model",
          id: reply.id,
          text: reply.text,
          candidates: reply.candidates,
        },
      ]);
    } catch {
      setMessage((prev) => [
        ...prev,
        {
          author: "system",
          id: generateId(),
          text: "Seems like it is not possible to send messages now",
          error: true,
        },
      ]);
    } finally {
      setInput("");
      setIsAgentReplying(false);
    }
  };

  return (
    <StyledPageContainer>
      <Header>
        <Typography variant="h1">
          {t("CHATBOT.CLINICAL_GUIDELINES.PAGE_TITLE")}
        </Typography>
        <Typography
          variant="body"
          style={{ marginLeft: defaultTheme.SPACING.XXS }}
        >
          {t("CHATBOT.CLINICAL_GUIDELINES.PAGE_INFORMATION")}
        </Typography>
        <Typography
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          variant="body-x-small"
          color="GRAY"
        >
          <Icon.Info
            style={{
              transform: "rotate(180deg)",
              marginRight: defaultTheme.SPACING.XS,
            }}
            color={defaultTheme.COLORS.RED}
          ></Icon.Info>
          {t("CHATBOT.CLINICAL_GUIDELINES.DISCLAMER_NO_PERSONAL_DATA")}
        </Typography>
      </Header>
      <ChatContainer>
        <Chat
          messages={messages}
          state="READY"
          sessionId={session?.sessionId}
          input={input}
          sources={session?.sources || []}
          isAgentReplying={isAgentReplying}
          onInputChange={setInput}
          onSend={handleSend}
          locale={locale}
          messageFeedback={messageFeedback}
          showFeedback={true}
          onThumbsFeedback={handleThumbsFeedback}
          informationMessage={
            showInformationMessage
              ? {
                  text: t(
                    "CHATBOT.CLINICAL_GUIDELINES.DISCLAMER_MESSAGES_RECORDED",
                  ),
                  severity: "info",
                }
              : undefined
          }
          onInformationMessageClose={() => {
            setShowInformationMessage(false);
          }}
          translations={{
            GREETING_TITLE: t("CHATBOT.CLINICAL_GUIDELINES.GREETING_TITLE"),
            GREETING_MESSAGE: t("CHATBOT.CLINICAL_GUIDELINES.GREETING_MESSAGE"),
          }}
        />
        <DisclaimerText variant="body-x-small">
          {t("CHATBOT.CLINICAL_GUIDELINES.DISCLAMER_ANSWER_ACCURACY")}
        </DisclaimerText>
      </ChatContainer>
    </StyledPageContainer>
  );
};
